import {
  isWorkbookErrorCell,
  type WorkbookCell,
  type WorkbookDefinition,
  type WorkbookSheet,
} from '@erp/shared-types';
import { resultColumns, type ColumnType } from '@erp/workbooks';
import type {
  ReportDataContextValue,
  ReportDatasetColumn,
  ReportDatasetRow,
  ReportDatasetValue,
} from '@/components/report-canvas/ReportDataContext';

/** Columns with their Declared types: a Worksheet's, or its Result's. */
type TypedColumns = {
  columns: readonly { name: string; type: ColumnType }[];
};

/** A Report dataset before its rows: which Worksheet's Result it charts, and its columns. */
export type WorkbookReportSheet = TypedColumns & {
  key: string;
  label: string;
  sheet: WorkbookSheet;
};

/**
 * The columns a Report charts from a Worksheet's Result: a columnar
 * Worksheet's Output columns (the same array, so conversions cache on it),
 * or a pivot's Result columns, whose Column keys can come from its data.
 */
export function workbookReportSheetColumns(
  sheet: WorkbookSheet,
  resultColumnNames?: readonly string[],
): TypedColumns['columns'] {
  return resultColumns(sheet, resultColumnNames);
}

/** The Report datasets a Definition offers: one per Worksheet, keyed by its id. */
export function workbookReportSheets(
  definition: Pick<WorkbookDefinition, 'sheets'>,
): WorkbookReportSheet[] {
  return definition.sheets.map((sheet) => ({
    key: sheet.id,
    label: sheet.name,
    sheet,
    columns: workbookReportSheetColumns(sheet),
  }));
}

const DATA_TYPES: Readonly<Record<ColumnType, string>> = {
  text: 'string',
  decimal: 'number',
  date: 'date',
  datetime: 'date',
  boolean: 'boolean',
};

/** A Worksheet's Output columns as Report dataset columns, typed by their Declared types. */
export function workbookReportColumns(
  sheet: TypedColumns,
): ReportDatasetColumn[] {
  return sheet.columns.map((column) => ({
    key: column.name,
    label: column.name,
    dataType: DATA_TYPES[column.type],
  }));
}

/**
 * A Result cell as a Report value of its Declared type. Results carry Number
 * and TRUE/FALSE values as text, to keep them exact across JSON; an Error cell
 * is null, as a chart has nothing to plot for it.
 */
export function workbookReportValue(
  cell: WorkbookCell,
  type: ColumnType,
): ReportDatasetValue {
  if (cell === null || isWorkbookErrorCell(cell)) return null;
  if (type === 'decimal') {
    if (cell === '') return null;
    const value = Number(cell);
    return Number.isFinite(value) ? value : null;
  }
  if (type === 'boolean')
    return typeof cell === 'boolean' ? cell : cell === 'true';
  return String(cell);
}

/**
 * Result rows (cells by position under `columns`) as Report dataset rows,
 * keyed by column name. A column the Worksheet doesn't declare reads as text.
 */
export function workbookReportRows(
  sheet: TypedColumns,
  columns: readonly string[],
  rows: readonly (readonly WorkbookCell[])[],
): ReportDatasetRow[] {
  const types = new Map(
    sheet.columns.map((column) => [column.name, column.type]),
  );
  const typed = columns.map((name) => ({
    name,
    type: types.get(name) ?? 'text',
  }));
  return rows.map((row) =>
    Object.fromEntries(
      typed.map(({ name, type }, index) => [
        name,
        workbookReportValue(row[index] ?? null, type),
      ]),
    ),
  );
}

/** The data context with one dataset's rows replaced, as a component past the Client row limit reads it. */
export function withDatasetRows(
  data: ReportDataContextValue,
  key: string,
  rows: ReportDatasetRow[],
  isLoading: boolean,
  aggregated = false,
): ReportDataContextValue {
  return {
    ...data,
    datasets: data.datasets.map((dataset) =>
      dataset.key === key
        ? { ...dataset, rows, isLoading, aggregated }
        : dataset,
    ),
  };
}
